/*
 * @Author: 张印 hbzyin@163.com
 * @FilePath: /mall-fe/src/pages/_app.tsx
 * @Description: 商城首页入口文件 服务端预渲染
 *
 */
import type { NextPage } from 'next';
import App from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useStateRef } from 'reactutils';
import { QueryClientProvider } from '@tanstack/react-query';

import { getProcureEnumsData, getSiteConfig } from 'api/std';
import {
  changeApp,
  checkUserHaveRead,
  getConfiguration,
  getRuntimeConfiguration,
  getUserDetail,
  showGoodsPricePermission,
} from 'api/user';

import Color from 'color';
import CartContextProvider from 'comps/CartContextProvider';
import CategoryProvider from 'comps/CategoryProvider';
import GeneralErrorBoundary from 'comps/GeneralErrorBoundary';
import { MainLayout } from 'comps/Layout';
import LocContextProvider from 'comps/LocContextProvider';
import MediaQueryProvider from 'comps/MediaQueryProvider';
import NoticeListModal from 'comps/NoticeListModal';
import OrderContextProvider from 'comps/OrderContextProvider';
import {
  ConfigurationContext,
  DeviceContext,
  ModalContext,
  ModalContextInterface,
  NavConfigContext,
  PriceContext,
  ProcureEnumDictionaryContext,
  RuntimeConfigurationContext,
  SiteContext,
  UserContext,
} from 'libs/contexts';
import { UserSource } from 'libs/enums';
import { webps } from 'libs/imgs';
import { initSdk } from 'libs/qywx';
import { captureError, useSentryContext } from 'libs/sentry';
import { useBaiduPv } from 'libs/baiduTongji';
import { updateSiteConfigFromPortalFurnish } from 'libs/std-util';
import { Skip } from 'libs/types';
import { ENV, getHref, isPT, STATIC_HREFS } from 'libs/urls';
import { amI, canI, isCurrentUserType, switchApp, USER_TYPE } from 'libs/user';
import { isInSltApp, isMobileMall, obsHost, ossHost, setObsHost, setOssHost } from 'libs/utils';
import { queryClient } from 'libs/react-query';

import theme from 'themes/index';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';

import dopAppInstance, { isInDop, rewriteWindowOpen } from 'libs/dopUtil.js';
import { YXYF_TOKEN } from 'libs/helper';

import 'assets/font-icon/iconfont.css';
import 'react-vant/lib/index.css';
import 'moment/locale/zh-cn';

moment.locale('zh-cn');

const DEFAULT_SCROLLBAR_WIDTH = '15px';

const isServer = typeof window === 'undefined';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
  layoutProps?: any;
};

// 提取宽度
function extractWidth(str: string) {
  if (!str) return DEFAULT_SCROLLBAR_WIDTH;
  return str.match(/[0-9]+px/)?.[0] ?? DEFAULT_SCROLLBAR_WIDTH;
}

// 设置全局样式
function getStdThemeStyles(primaryColor) {
  return `
    :root {
      --primaryColor: ${primaryColor};
      --fontPrimaryColor: ${primaryColor};
      --noticeCardTag: ${primaryColor};
      --smsVerTextColor: ${primaryColor};
      --smsVerBackground: ${Color(primaryColor).rotate(5).fade(0.91)};
      --smsVerBorderColor: ${Color(primaryColor).lighten(0.3)};
    }
  `;
}

// 路由白名单：满足条件时不进行 changeApp 后的跳转
// 处理小程序、商链通等容器内mallfe的页面跳转偶先直接跳到我的页面
const NO_REDIRECT_PATHS: ReadonlySet<string> = new Set([
  '/logistics-map',
  '/meeting',
  '/pages/welfare',
  '/message-news/detail/[id]',
]);

// 首页导航配置
export function generateNavConfig(config: Partial<SiteConfig>): NavConfigInterface {
  if (!config) {
    return {} as any;
  }
  // 商城首页导航配置类型
  const nav: NavConfigInterface = {
    header: [],
    leftNav: [],
    hotWords: [],
    logo: [],
    miniHeader: [],
    footer: [],
    headerInMobile: [],
  };

  nav.logo.push({
    imageUrl: config.global?.logoUrl,
    roleType: 15,
    showInMobile: false,
    name: 'logo',
    link: '/',
    target: '_top',
    comment: '',
    isExternalUrl: true,
  });

  //  商城首页导航配置信息
  const modules: Nav[] = [
    {
      name: '首页',
      link: '/',
      target: '_top',
      comment: '',
      isExternalUrl: false,
      roleType: 15,
      showInMobile: false,
    },
    config.basic?.enableModuleTender
      ? {
          name: config.global?.tenderItemName,
          link: config.global?.tenderItemUrl ? config.global?.tenderItemUrl : '/tender/hall',
          target: config.global?.tenderItemUrl ? '_blank' : '_top',
          comment: '',
          isExternalUrl: !!config.global?.tenderItemUrl,
          roleType: 15,
          showInMobile: false,
        }
      : null,
    config.basic?.enableModuleGroupBuy
      ? {
          name: config.global?.groupBuyItemName,
          link: config.global?.groupBuyItemUrl ? config.global.groupBuyItemUrl : '/groupbuy',
          target: config.global?.groupBuyItemUrl ? '_blank' : '_top',
          comment: '',
          isExternalUrl: !!config.global?.groupBuyItemUrl,
          roleType: 15,
          showInMobile: false,
        }
      : null,
    config.basic?.enableModuleMall
      ? {
          name: config.global?.mallItemName,
          link: config.global?.mallItemUrl
            ? config.global.mallItemUrl
            : `//${config.basic?.mallDomain}`,
          target: '_blank',
          comment: '',
          isExternalUrl: true,
          roleType: 15,
          showInMobile: false,
        }
      : null,
    config.basic?.enableModuleFinancial
      ? {
          name: config.global?.financeItemName,
          link: config.global?.financeItemUrl ? config.global.financeItemUrl : '/scf',
          target: config.global?.financeItemUrl ? '_blank' : '_top',
          comment: '',
          isExternalUrl: !!config.global?.financeItemUrl,
          roleType: 15,
          showInMobile: false,
        }
      : null,
  ].filter(Boolean);

  nav.header.push(...modules);

  return nav;
}

export default function WebApp({
  Component,
  pageProps,
  userInfo,
  configuration,
  runtimeConfiguration,
  navConfig,
  siteConfig,
  deviceInfo,
  isMobile,
  priceData,
  procureEnumDictionary,
}: {
  Component: NextPageWithLayout;
  pageProps: any;
  userInfo: any;
  configuration: any;
  runtimeConfiguration: any;
  navConfig: any;
  siteConfig: any;
  deviceInfo: DeviceContextInterface;
  isMobile: boolean;
  priceData: PriceContextValue;
  procureEnumDictionary: Record<string, any>;
  openId: string;
  // miniKey: string;
}) {
  const [visibleNoticeListModal, setVisibleNoticeListModal] = useState(false);
  // 用于给页面组件Component套上一层布局组件
  const getLayout = Component.getLayout ?? ((page) => page);
  // 关于布局组件的额外信息
  const { layoutProps } = Component;

  const router = useRouter();
  // 现在登录后可能回到任意页面
  // useRecallToBind(userInfo);

  const isNoRedirectPath = (pathname: string): boolean => {
    return NO_REDIRECT_PATHS.has(pathname);
  };

  const [modalContextValueRef, setModalContextValue] = useStateRef<ModalContextInterface>({
    onModal: false,
  });

  useSentryContext(userInfo, siteConfig);
  useBaiduPv();

  // 分供方门户 对接
  useEffect(() => {
    const urlSearchStr = window.location.search;
    if (urlSearchStr.includes('refer=supplierPortal')) {
      console.log('确定是分供方进来的');
      if (!!userInfo && urlSearchStr.includes('authTicket=')) {
        // 交给后台登录，并重定向会原地址
        let destUrl = `/sso/login?return_url=${encodeURIComponent(window.location.href)}`;
        const resultUrl = `${window.location.origin}/${destUrl}`;
        console.log('result url', resultUrl);
        window.location.href = resultUrl;
      }
    }
  }, []);

  const initDopUrl = async () => {
    const urlSearchStr = window.location.search;
    try {
      const inDop = await isInDop();
      if (!inDop) {
        return;
      }
      if (urlSearchStr.includes('refer=dop')) {
        let destUrl = window.location.href?.replace('refer=dop', '');
        // 解析 transferUrl 参数，如果存在则使用 transferUrl 作为 destUrl
        const urlParams = new URLSearchParams(urlSearchStr);
        const transferUrl = urlParams.get('transferUrl');

        if (!userInfo) {
          const { accessToken } = await dopAppInstance.getToken();
          if (transferUrl) {
            destUrl = decodeURIComponent(transferUrl);
          }
          destUrl = destUrl.includes('?')
            ? `${destUrl}&idaasToken=${accessToken}`
            : `${destUrl}?idaasToken=${accessToken}`;
          destUrl = `/sso/login?return_url=${encodeURIComponent(destUrl)}`;

          const resultUrl = `${window.location.origin}${destUrl}`;
          console.log('resultUrl:', resultUrl);
          // window.selfOpen(resultUrl);
          window.location.href = resultUrl;
        } else {
          // NOTE: 已登录，并且存在 transferUrl 参数，则直接跳转
          if (transferUrl) {
            window.location.href = transferUrl;
          }
        }
      } else if (urlSearchStr.includes('jumpInDop=true')) {
        // 保证第一次登录进来不重复定位菜单
        dopAppInstance.changeMenu({
          menuKey: '/?refer=dop',
        });
      }
    } catch (e) {
      console.error('dop check: ', e);
    }
  };

  // DOP嵌入
  useEffect(() => {
    initDopUrl();
  }, []);

  // useEffect(() => {
  //   // 全局错误拦截打点
  //   window.onerror = (_msg, _source, _line, _colnum, err) => {
  //     err &&
  //       !('code' in err) &&
  //       mapStackTrace(err.stack, (stack) => {
  //         const mapped = stack.join('\n');
  //         recordError({ message: `${err.message} - ${mapped}` });
  //       });
  //   };
  //   window.onunhandledrejection = function (e) {
  //     try {
  //       if (e?.reason && 'code' in e.reason) return;
  //       e?.reason?.stack &&
  //         mapStackTrace(e?.reason?.stack, (stack) => {
  //           const mapped = stack.join('\n');
  //           recordError({ message: `${e.reason} - ${mapped}` });
  //         });
  //     } catch (err) {
  //       console.error('onunhandledrejection error: ', err);
  //     }
  //   };
  // }, []);

  useEffect(() => {
    if (isMobile && userInfo !== null && amI(userInfo, 'operation')) {
      const myApp = userInfo?.applications ?? [];
      if (canI(userInfo, 'purchaser')) {
        const { sysNo } = myApp.find((a) => a.key === 'purchaser');
        changeApp(sysNo).then(() => {
          // 如果在白名单页面，则不进行跳转
          if (isNoRedirectPath(router.pathname)) {
            return;
          } else {
            window.location.href = getHref('/mine');
          }
        });
      } else if (canI(userInfo, 'supplier')) {
        const { sysNo } = myApp.find((a) => a.key === 'supplier');
        changeApp(sysNo).then(() => {
          if (isNoRedirectPath(router.pathname)) {
            return;
          } else {
            window.location.href = getHref('/mine');
          }
        });
      } else {
        // TODO
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userInfo) return;
    if (!isInSltApp(window.navigator.userAgent)) return;
    if (isCurrentUserType(userInfo, USER_TYPE.SUPPLIER)) return;

    const flag = 'slt-switch-done';
    if (sessionStorage.getItem(flag)) {
      sessionStorage.removeItem(flag);
      return;
    }

    switchApp(userInfo, 'supplier', () => {
      sessionStorage.setItem(flag, '1');
      window.location.reload();
    });
  }, [userInfo]);

  useEffect(() => {
    // 本地开发环境不执行
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    rewriteWindowOpen();

    isInDop().then((res) => {
      console.log('isInDop: ', res);
      if (res) {
        // dop监听初始化
        dopAppInstance?.on('changePath', ({ data }) => {
          console.log('change path listen... mallfe', data);
          const { shortUrl } = data;
          window.open(shortUrl, '_top');
        });
      }
    });
  }, []);

  useEffect(() => {
    function onMutations(mutations) {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          const bodyOnModal = document.body.classList.contains('ant-scrolling-effect');
          if (bodyOnModal !== modalContextValueRef.current.onModal) {
            setModalContextValue({
              onModal: bodyOnModal,
              width: bodyOnModal ? extractWidth(document.body.style.width) : undefined,
            });
          }
        }
      }
    }

    const observer = window.MutationObserver && new MutationObserver(onMutations);
    observer?.observe(document.body, { attributes: true });

    return () => {
      observer?.disconnect();
    };
    // eslint-disable-next-line
  }, []);
  // dev、st环境开启测试工具
  useEffect(() => {
    const openToolsdevs = ['zjdev', 'zjst'];
    const curDev = ENV;
    const isNeedDev = isMobile && openToolsdevs.includes(curDev);
    let vConsole;
    if (isNeedDev) {
      try {
        vConsole = require('vconsole');
        vConsole = new vConsole({ maxLogNumber: 1000 });
      } catch (error) {
        console.error(`vconsole 异常:${error}`);
      }
    }
    return () => {
      isNeedDev && vConsole?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 解析企业微信中后端返回的token
  // 跳转到严选优服需要拼接token保证登录信息同步
  useEffect(() => {
    const tempUrl = new URL(window.location.href);
    const token = tempUrl.searchParams?.get('token');
    localStorage.setItem(YXYF_TOKEN, token || '');
  }, []);

  useEffect(() => {
    initSdk();
  }, [router.pathname]);

  useEffect(() => {
    /**
     * 只要有公告弹窗，
     * 每访问一个页面都必须弹，
     * 本地开发环境就不弹公告弹窗了
     */
    if (
      !isMobile &&
      process.env.NODE_ENV !== 'development' &&
      router.pathname !== '/pages/footer'
    ) {
      checkUserHaveReadNotice();
    }
  }, [router.pathname, isMobile]);

  // 校验是否需要公告弹窗
  async function checkUserHaveReadNotice() {
    const res = await checkUserHaveRead();
    if (res.data) {
      setVisibleNoticeListModal(true);
    }
  }

  function buildImgSrc(src: string): string {
    return this.supportsWebp && webps[src] ? webps[src] : src;
  }

  const [deviceData, setDeviceData] = useState<DeviceContextInterface>(() => {
    const data = { ...deviceInfo };
    data.buildImgSrc = buildImgSrc.bind(data);
    return data;
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    setDeviceData((deviceData) => {
      const data = { ...deviceInfo, supportsWebp: deviceData.supportsWebp };
      data.buildImgSrc = buildImgSrc.bind(data);
      return data;
    });
  }, [deviceInfo]);
  // 加载全局样式配置
  const themeStyles = theme;
  const platformMainColor = siteConfig?.global?.platformMainColor || '#018DD7';
  const stdThemeStyles = getStdThemeStyles(platformMainColor);

  let staticHref;
  if (isPT) {
    staticHref = `//${runtimeConfiguration?.find((item) => item.key === 'static-host')?.value}`;
  } else {
    staticHref = STATIC_HREFS[ENV];
  }

  const antdCSSFile = `${staticHref}/framework/antd4-${platformMainColor?.substring(1)}.css`;

  if (!ossHost) {
    setOssHost(runtimeConfiguration?.find((config) => config.key === 'oss')?.value);
  }
  if (!obsHost) {
    setObsHost(runtimeConfiguration?.find((config) => config.key === 'obs')?.value);
  }

  if (router.pathname === '/logistics-map' || router.pathname === '/download-shb') {
    return (
      <>
        <Head>
          <link rel="stylesheet" href={antdCSSFile} />
        </Head>
        <GeneralErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <DeviceContext.Provider value={deviceData}>
              <Component {...pageProps} />
            </DeviceContext.Provider>
          </QueryClientProvider>

          <style jsx global>{`
            ${themeStyles}
            ${stdThemeStyles}

            * {
              padding: 0;
              margin: 0;
              outline: none;
              list-style: none;
              box-sizing: border-box;
            }

            a {
              text-decoration: none;
            }

            html,
            body {
              -webkit-overflow-scrolling: touch;
            }

            .clearfix:before,
            .clearfix:after {
              content: "";
              display: table;
            }

            .clearfix:after {
              clear: both;
            }
            .ellipsis {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .simple-bottom-transition-enter {
              transform: translateY(100%);
            }

            .simple-bottom-transition-enter-active {
              transform: translateY(0);
              transition: transform 0.3s;
            }

            .simple-bottom-transition-enter-done {
              transform: translateY(0);
            }

            .simple-bottom-transition-exit {
              transform: translateY(0);
            }

            .simple-bottom-transition-exit-active {
              transform: translateY(100%);
              transition: transform 0.3s;
            }

            .simple-bottom-transition-exit-done {
              transform: translateY(100%);
            }

            .simple-right-transition-enter {
              transform: translateX(100%);
            }

            .simple-right-transition-enter-active {
              transform: translateX(0);
              transition: transform 0.3s;
            }

            .simple-right-transition-enter-done {
              transform: translateX(0);
            }

            .simple-right-transition-exit {
              transform: translateX(0);
            }

            .simple-right-transition-exit-active {
              transform: translateX(100%);
              transition: transform 0.3s;
            }

            .simple-right-transition-exit-done {
              transform: translateX(100%);
            }

            /* 定制react-vant 样式 */
            :root {
              --rv-primary-color: ${platformMainColor};
              --rv-field-placeholder-text-color: #bbbbbb;
              --rv-calendar-selected-day-background-color: ${platformMainColor};
              --rv-button-danger-background-color: ${platformMainColor};
              --rv-button-danger-border-color: ${platformMainColor};
              --rv-action-bar-button-danger-color: ${platformMainColor};
              --rv-tabbar-height: 70px;
            }
            /* 定制react-vant dialog button 样式 */
            .rv-dialog__footer.rv-action-bar {
              /*移动端兼容性*/
              gap: 16px;
            }
            .rv-dialog__footer .rv-action-bar-button {
              border-radius: 18px;
            }
            .rv-dialog__footer .rv-dialog__cancel {
              border: 1px solid #e5e5e5;
              color: #333;
              background-color: #fff;
              background-image: none;
            }
            .rv-custom-dialog .rv-custom-children {
              padding: var(--rv-dialog-has-title-message-padding-top)
                var(--rv-padding-lg) var(--rv-padding-md);
              text-align: center;
              font-size: 14px;
              color: var(--rv-text-color);
            }
            .rv-custom-dialog .rv-custom-children .icon {
              margin-right: 6px;
              font-size: 18px;
            }
            .rv-custom-dialog .rv-custom-children .warning {
              color: #f1a024;
            }
          `}</style>
        </GeneralErrorBoundary>
      </>
    );
  }

  return (
    <>
      <Head>
        <link rel="stylesheet" href={antdCSSFile} />
      </Head>

      <GeneralErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <UserContext.Provider value={userInfo}>
            <ConfigurationContext.Provider value={configuration}>
              <RuntimeConfigurationContext.Provider value={runtimeConfiguration}>
                <NavConfigContext.Provider value={navConfig}>
                  <ModalContext.Provider value={modalContextValueRef.current}>
                    <MediaQueryProvider isMobile={isMobile}>
                      <SiteContext.Provider value={siteConfig}>
                        <LocContextProvider>
                          <CategoryProvider>
                            <CartContextProvider>
                              <OrderContextProvider>
                                <DeviceContext.Provider value={deviceData}>
                                  <PriceContext.Provider value={priceData}>
                                    <ProcureEnumDictionaryContext.Provider
                                      value={procureEnumDictionary}
                                    >
                                      {/* <NProgress> */}
                                      <MainLayout getLayout={getLayout} layoutProps={layoutProps}>
                                        <Component {...pageProps} />
                                      </MainLayout>
                                      <NoticeListModal
                                        visible={visibleNoticeListModal}
                                        setVisible={setVisibleNoticeListModal}
                                      />
                                      {/* </NProgress> */}
                                    </ProcureEnumDictionaryContext.Provider>
                                  </PriceContext.Provider>
                                </DeviceContext.Provider>
                              </OrderContextProvider>
                            </CartContextProvider>
                          </CategoryProvider>
                        </LocContextProvider>
                      </SiteContext.Provider>
                    </MediaQueryProvider>
                  </ModalContext.Provider>
                </NavConfigContext.Provider>
              </RuntimeConfigurationContext.Provider>
            </ConfigurationContext.Provider>
          </UserContext.Provider>
        </QueryClientProvider>

        <style jsx global>{`
          ${themeStyles}
          ${stdThemeStyles}

          * {
            padding: 0;
            margin: 0;
            outline: none;
            list-style: none;
            box-sizing: border-box;
          }

          a {
            text-decoration: none;
          }

          html,
          body {
            -webkit-overflow-scrolling: touch;
          }

          .clearfix:before,
          .clearfix:after {
            content: "";
            display: table;
          }

          .clearfix:after {
            clear: both;
          }
          .ellipsis {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .simple-bottom-transition-enter {
            transform: translateY(100%);
          }

          .simple-bottom-transition-enter-active {
            transform: translateY(0);
            transition: transform 0.3s;
          }

          .simple-bottom-transition-enter-done {
            transform: translateY(0);
          }

          .simple-bottom-transition-exit {
            transform: translateY(0);
          }

          .simple-bottom-transition-exit-active {
            transform: translateY(100%);
            transition: transform 0.3s;
          }

          .simple-bottom-transition-exit-done {
            transform: translateY(100%);
          }

          .simple-right-transition-enter {
            transform: translateX(100%);
          }

          .simple-right-transition-enter-active {
            transform: translateX(0);
            transition: transform 0.3s;
          }

          .simple-right-transition-enter-done {
            transform: translateX(0);
          }

          .simple-right-transition-exit {
            transform: translateX(0);
          }

          .simple-right-transition-exit-active {
            transform: translateX(100%);
            transition: transform 0.3s;
          }

          .simple-right-transition-exit-done {
            transform: translateX(100%);
          }

          /* 定制react-vant 样式 */
          :root {
            --rv-primary-color: ${platformMainColor};
            --rv-field-placeholder-text-color: #bbbbbb;
            --rv-calendar-selected-day-background-color: ${platformMainColor};
            --rv-button-danger-background-color: ${platformMainColor};
            --rv-button-danger-border-color: ${platformMainColor};
            --rv-action-bar-button-danger-color: ${platformMainColor};
            --rv-tabbar-height: 70px;
          }
          /* 定制react-vant dialog button 样式 */
          .rv-dialog__footer.rv-action-bar {
            /*移动端兼容性*/
            gap: 16px;
          }
          .rv-dialog__footer .rv-action-bar-button {
            border-radius: 18px;
          }
          .rv-dialog__footer .rv-dialog__cancel {
            border: 1px solid #e5e5e5;
            color: #333;
            background-color: #fff;
            background-image: none;
          }
          .rv-custom-dialog .rv-custom-children {
            padding: var(--rv-dialog-has-title-message-padding-top)
              var(--rv-padding-lg) var(--rv-padding-md);
            text-align: center;
            font-size: 14px;
            color: var(--rv-text-color);
          }
          .rv-custom-dialog .rv-custom-children .icon {
            margin-right: 6px;
            font-size: 18px;
          }
          .rv-custom-dialog .rv-custom-children .warning {
            color: #f1a024;
          }
        `}</style>
      </GeneralErrorBoundary>
    </>
  );
}

const handleCaptureError = (e) => {
  if (isServer && !(e.code === 403 || e.code === 401 || e.code === 400101)) {
    captureError(e);
  }
};

WebApp.getInitialProps = async (appContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);
  const skip: Skip = appContext?.Component?.skip;

  const serverSide = !!appContext.ctx.req;
  let serverHeaders = appContext.ctx.req?.headers;
  //   服务端请求设置cookie
  //   const { wxCode: QwxCode, needBind: QneedBind } = appContext?.ctx?.req?.query || {};
  // const { openId, needBind, miniKey } = appContext?.ctx?.req?.query || {};

  // 默认三局租户cscec3b
  // const target = process.env.TARGET;
  let userInfo: UserInfo = null;
  let configuration = [];
  let runtimeConfiguration = [];
  let navConfig = {};
  let siteConfig: Partial<SiteConfig> = null;
  let priceData: PriceContextValue = {
    showPrice: false,
    tip: '',
    showSellCount: false,
    showScore: false,
  };
  let procureEnumDictionary;
  if (skip?.request !== true) {
    // [获取用户信息，获取运营配置信息，获取oss配置，获取首页导航配置，获取商品分类，获取商品价格展示权限]
    await Promise.all([
      (async () => {
        try {
          const userRes = await getUserDetail({
            query: { _t: Date.now() },
            serverSide,
            headers: serverHeaders,
          });
          if (userRes.code === 200) {
            const data = userRes.data.profile;
            userInfo = {
              name: data.displayName,
              isPurchaser: data.isPurchaser,
              isSupplier: data.isSupplier,
              isSystemUser: data.isSystemUser,
              permissions: userRes.data.permissions,
              organizationRoleType: data.organizationRoleType,
              mobile: data.mobile,
              avatar: data.avatar,
              status: data.status,
              supplierStatus: data.supplierStatus,
              purchaserStatus: data.purchaserStatus,
              organizationName: data.organizationName,
              organizationCode: data.organizationCode,
              organizationSysNo: data.organizationSysNo,
              supplierSysNo: data.supplierSysNo || null,
              applications: userRes.data?.applications || [],
              completeInfo: userRes.data,
              organizationSource: data.organizationSource,
              isInside: data.organizationSource === UserSource.inside,
              isPersonal: data.isPersonal,
              currentUserType: data?.currentUserType,
              realAuthentication: data?.realAuthentication,
              userAuditType: data.userAuditType,
              orgAuditType: data.orgAuditType,
              sysNo: data.sysNo,
              selfOrgCode: data.selfOrgCode,
              customizeOrgFlag: data.customizeOrgFlag,
            };
          }
        } catch (e) {
          handleCaptureError(e);
        }
      })(),
      (async () => {
        try {
          const configRes = await getConfiguration({
            query: { _t: Date.now() },
            serverSide,
            headers: serverHeaders,
          });
          if (configRes.code === 200) {
            configuration = configRes.data;
          }
        } catch (e) {
          handleCaptureError(e);
        }
      })(),
      (async () => {
        try {
          const configRes = await getRuntimeConfiguration({
            serverSide,
            headers: serverHeaders,
          });
          if (configRes.code === 200) {
            runtimeConfiguration = configRes.data || runtimeConfiguration;
          }
        } catch (e) {
          handleCaptureError(e);
        }
      })(),
      (async () => {
        try {
          siteConfig = await getSiteConfig({
            serverSide,
            headers: serverHeaders,
          });

          navConfig = generateNavConfig(siteConfig);

          if (serverSide) {
            appContext.ctx.req._tenantCode = siteConfig.tenantCode;
          }
        } catch (e) {
          siteConfig = {};
          navConfig = {};
          handleCaptureError(e);
        }
      })(),
      // NOTE: 商品分类数据已迁移至客户端（使用 React Query）
      // 原因：2.5MB 数据在服务端获取会阻塞首屏渲染，降低性能
      // 迁移方案：使用 CategoryProvider 在客户端获取并缓存
      // 详见：CATEGORY_REFACTOR.md
      (async () => {
        try {
          const goodsPriceRes = await showGoodsPricePermission({
            serverSide,
            headers: serverHeaders,
          });
          if (goodsPriceRes.code === 200 && goodsPriceRes.data != null) {
            priceData = goodsPriceRes.data;
          }
        } catch (e) {
          handleCaptureError(e);
        }
      })(),
      (async () => {
        try {
          const { code, data } = await getProcureEnumsData({
            serverSide,
            headers: serverHeaders,
          });

          if (code === 200 && data != null) {
            procureEnumDictionary = data;
          }
        } catch (e) {
          handleCaptureError(e);
        }
      })(),
    ]);

    const { preview } = appContext.ctx.query;
    const newSiteConfig = await updateSiteConfigFromPortalFurnish(
      siteConfig,
      runtimeConfiguration,
      {
        serverSide,
        headers: serverHeaders,
        query: {
          preview: !!preview,
        },
      },
    );
    if (newSiteConfig !== siteConfig) {
      siteConfig = newSiteConfig;
      navConfig = generateNavConfig(siteConfig);
    }
  }

  const deviceInfo: any = {
    userAgent:
      typeof navigator === 'undefined'
        ? appContext.ctx?.req?.headers?.['user-agent']
        : navigator.userAgent,
    supportsWebp: serverSide
      ? appContext.ctx?.req?.headers?.accept?.includes('image/webp')
      : document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0,
  };

  return {
    ...appProps,
    userInfo,
    configuration,
    runtimeConfiguration,
    siteConfig,
    navConfig,
    deviceInfo,
    isMobile: isMobileMall(appContext.ctx?.req),
    priceData,
    procureEnumDictionary,
    // openId,
    // needBind,
    // miniKey,
  };
};
